import React, { useState } from 'react';

const InternalAuditServices = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: 'I need help with Internal Audit Services.',
    website_hp: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  try {
    const response = await fetch("https://akiradvisory.in/send_mail.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      setFormStatus('success');
      // Reset form while keeping the default message
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: 'I need help with Internal Audit Services.',
        website_hp: ''
      });
    } else {
      setFormStatus('error');
    }
  } catch (error) {
    setFormStatus('error');
  } finally {
    setIsSubmitting(false);
    setTimeout(() => setFormStatus(''), 5000);
  }
};

  return (
    <main>
      {/* Hero Section */}
      <section className="relative bg-blue-700 pt-16 pb-12">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:w-2/3">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Internal Audit Services
            </h1>

            <p className="mt-4 text-xl text-blue-100">
              Comprehensive internal audit solutions to strengthen your governance, risk management, and internal controls. Our expert auditors provide independent assurance and valuable insights to improve business processes and compliance.
            </p>
            <div className="mt-8">
              <a href="#contact-section" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                  <span className="mr-2">🛡️</span> Strengthen Your Controls
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Navigation */}
      <section className="bg-gray-50 border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex space-x-6 overflow-x-auto text-sm font-medium">
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#details">Overview & Benefits</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#procedure">Our Process</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#documents">What We Need</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#you-get">What You Get</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#service-faqs">FAQs</a></li>
            </ul>
        </div>
      </section>

      {/* Overview Section */}
      <section id="details" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 border-b pb-2">
                Overview of Internal Audit Services
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Professional internal audit services provide independent, objective assurance and consulting services designed to add value and improve your organization's operations. Our internal audit approach helps organizations accomplish their objectives by bringing a systematic, disciplined approach to evaluate and improve the effectiveness of risk management, control, and governance processes.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our comprehensive internal audit services cover financial controls, operational processes, IT systems, compliance requirements, and strategic initiatives. We provide actionable recommendations to strengthen internal controls, improve efficiency, and ensure regulatory compliance across your organization.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you need a complete internal audit function outsourcing, co-sourcing support for your existing team, or specific process audits, our experienced internal auditors bring deep industry knowledge and technical expertise to deliver meaningful insights and practical solutions.
              </p>

              <div className="mt-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">
                  Key Features & Benefits
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Risk-Based Audit Approach:</strong> Focus on high-risk areas to provide maximum value and assurance coverage.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Comprehensive Coverage:</strong> Financial, operational, IT, and compliance audits tailored to your industry.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Regulatory Compliance:</strong> Ensure adherence to statutory requirements, accounting standards, and industry regulations.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Process Improvement:</strong> Identify inefficiencies and recommend process enhancements for cost savings.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Fraud Prevention:</strong> Detect control weaknesses and implement measures to prevent fraud and errors.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Technology Integration:</strong> Leverage data analytics and audit technology for comprehensive coverage.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Management Insights:</strong> Provide strategic insights to management and audit committee for informed decision-making.
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-1" id="contact-section">
              <div className="bg-gray-50 p-6 rounded-xl shadow-xl border-t-4 border-blue-600 sticky top-28">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Quick Enquiry
                </h3>
                
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-100 text-green-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">✅</span>
                    Thank you! Your enquiry has been submitted. We'll contact you shortly.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-100 text-red-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">❌</span>
                    There was an error submitting your form. Please try again.
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleFormSubmit}>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Full Name" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <textarea 
                    rows="3" 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="I need help with Internal Audit Services." 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required
                  ></textarea>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3 inline-block"></div>
                        Submitting...
                      </>
                    ) : (
                      'Request an Initial Consultation'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Procedure Section */}
      <section id="procedure" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Our Comprehensive Process
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              8 Steps for Internal Audit
            </p>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 grid-flow-row-dense">
            {[
              { number: "01", icon: "📋", title: "Planning & Scoping", desc: "Understand business objectives, assess risks, and develop risk-based audit plan and scope." },
              { number: "02", icon: "📄", title: "Documentation Review", desc: "Examine policies, procedures, previous audit reports, and process documentation." },
              { number: "03", icon: "🔍", title: "Fieldwork & Testing", desc: "Conduct detailed testing of controls, transactions, and processes through sample testing and data analysis." },
              { number: "04", icon: "⚠️", title: "Issue Identification", desc: "Identify control gaps, compliance issues, process inefficiencies, and potential risk areas." },
              { number: "05", icon: "👥", title: "Management Discussion", desc: "Discuss preliminary findings with process owners and management for validation and context." },
              { number: "06", icon: "📝", title: "Report Preparation", desc: "Prepare detailed audit report with findings, risk assessment, and actionable recommendations." },
              { number: "07", icon: "📊", title: "Reporting & Presentation", desc: "Present findings to management and audit committee with emphasis on significant issues." },
              { number: "08", icon: "🔄", title: "Follow-up & Monitoring", desc: "Track implementation of recommendations and verify effectiveness of corrective actions." }
            ].map((step, index) => (
              <div key={index} className="sm:col-span-2 lg:col-span-2 flex flex-col justify-between p-3 bg-white rounded-xl shadow-md text-center hover:shadow-lg transition-shadow border-t-4 border-blue-500">
                <div className="flex flex-col flex-grow justify-center">
                  <span className="text-xl font-extrabold text-blue-600 block leading-tight">{step.number}</span>
                  <span className="text-2xl mx-auto block leading-none">{step.icon}</span>
                  <h3 className="text-base font-semibold text-gray-900 leading-snug mt-0.5">{step.title}</h3>
                  <p className="text-gray-600 text-sm text-justify mt-1 leading-snug">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Information We Need
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Essential Audit Documentation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "🏢", title: "Organizational Structure", desc: "Company organization charts, reporting relationships, and delegation of authority matrix." },
              { icon: "📋", title: "Policies & Procedures", desc: "All company policies, standard operating procedures, and internal control documentation." },
              { icon: "📊", title: "Financial Records", desc: "Financial statements, trial balances, general ledgers, and supporting documentation." },
              { icon: "🛡️", title: "Compliance Documents", desc: "Regulatory filings, tax returns, licenses, and compliance certificates." },
              { icon: "💻", title: "IT Systems Information", desc: "System documentation, access controls, and IT governance frameworks." },
              { icon: "📜", title: "Previous Audit Reports", desc: "Internal and external audit reports from previous periods and management responses." },
              { icon: "🎯", title: "Risk Assessments", desc: "Company risk registers, risk assessments, and mitigation strategies." },
              { icon: "👥", title: "Board & Committee Minutes", desc: "Minutes of board, audit committee, and other key committee meetings." },
              { icon: "📈", title: "Performance Metrics", desc: "Key performance indicators, operational metrics, and management reports." }
            ].map((doc, index) => (
              <div key={index} className="p-6 border rounded-xl shadow-md flex items-start space-x-4 h-full">
                <span className="text-2xl flex-shrink-0 mt-1">{doc.icon}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
                  <p className="text-gray-600 text-sm mt-1 text-justify">{doc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section id="you-get" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Your Deliverables
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              What Akir Advisory Delivers
            </p>
          </div>

          {/* 5 equal boxes in a single row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { icon: "📄", title: "Comprehensive Audit Report", desc: "Detailed audit report with findings, risk ratings, root cause analysis, and management action plans." },
              { icon: "📊", title: "Risk Assessment Matrix", desc: "Prioritized risk assessment highlighting high-risk areas requiring immediate management attention." },
              { icon: "🛡️", title: "Control Improvement Plan", desc: "Practical recommendations to strengthen internal controls and mitigate identified risks." },
              { icon: "📈", title: "Process Efficiency Report", desc: "Identification of process inefficiencies and recommendations for operational improvements." },
              { icon: "🎧", title: "Ongoing Audit Support", desc: "Implementation guidance, follow-up audits, control testing support, audit committee presentations" }
            ].map((item, index) => (
              <div key={index} className="p-5 bg-white rounded-xl shadow-lg border-l-4 border-green-500 flex flex-col h-full">
                <span className="text-2xl mb-2 block">{item.icon}</span>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-snug">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="service-faqs" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                  <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
                      Common Questions
                  </h2>
                  <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                      Internal Audit FAQs
                  </p>
              </div>
              
              <div className="space-y-4 max-w-4xl mx-auto">
                {[
                  { 
                    question: "What is the difference between internal audit and external audit?", 
                    answer: "Internal audit focuses on evaluating and improving risk management, controls, and governance processes for management's benefit. External audit provides independent opinion on financial statements for stakeholders. Internal audit is forward-looking and advisory, while external audit is historical and assurance-focused." 
                  },
                  { 
                    question: "How often should internal audits be conducted?", 
                    answer: "Frequency depends on risk assessment - high-risk areas may require quarterly audits, medium-risk areas semi-annually, and low-risk areas annually. Most organizations benefit from a continuous audit approach with different processes audited throughout the year based on a risk-based audit plan." 
                  },
                  { 
                    question: "What areas can internal audit cover beyond financial controls?", 
                    answer: "Modern internal audit covers operational efficiency, IT systems and cybersecurity, compliance with laws and regulations, fraud prevention, strategic initiatives, environmental compliance, health and safety, and third-party vendor management. The scope is tailored to organizational objectives and risks." 
                  },
                  { 
                    question: "Can small businesses benefit from internal audit services?", 
                    answer: "Absolutely. Small businesses often have limited segregation of duties and benefit significantly from independent internal audit to strengthen controls, prevent fraud, ensure compliance, and improve processes. We offer scalable solutions tailored to business size and complexity." 
                  },
                  { 
                    question: "How do you measure the effectiveness of internal audit?", 
                    answer: "Effectiveness is measured through implementation rates of recommendations, reduction in control deficiencies, feedback from management and audit committee, cost savings identified, risk mitigation achieved, and alignment with organizational objectives. We provide regular effectiveness assessments." 
                  },
                  { 
                    question: "What qualifications do your internal auditors possess?", 
                    answer: "Our internal audit team includes Chartered Accountants, Certified Internal Auditors (CIA), Certified Information Systems Auditors (CISA), Certified Fraud Examiners (CFE), and professionals with relevant industry experience. We maintain continuous training on emerging risks and audit methodologies." 
                  }
                ].map((faq, index) => (
                  <div key={index} className="border rounded-lg p-5 shadow-sm">
                    <h4 className="font-semibold text-lg text-gray-900 mb-2">{faq.question}</h4>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                ))}
              </div>
          </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-blue-700/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-white">
                Ready to Strengthen Your Internal Controls?
            </h2>
            <p className="mt-4 text-xl text-blue-100 max-w-3xl mx-auto">
                Let our experienced internal auditors provide independent assurance and valuable insights to improve your governance, risk management, and control environment.
            </p>
            <a href="#contact-section" className="mt-8 inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                <span className="mr-2">🛡️</span> Schedule Internal Audit Discussion
            </a>
        </div>
      </section>
    </main>
  );
};

export default InternalAuditServices;